// Screen 3b · Class roster — paste the class list, rename or remove a name.
// Roster paste is ALLOWED here (ModuleD spec §4.4); names are display-only — no scores (rail #5).
// Calm + one-thumb: remove is a quiet two-tap (no red alarm), a removed name is easily re-pasted.

import { api } from "../api";
import { esc } from "../dom";

interface Student {
  id: string;
  display_name: string;
}
interface RosterResponse {
  class: { id: string; name: string; level: string | null };
  students: Student[];
}

export async function renderClassView(app: HTMLElement, params: Record<string, string>): Promise<void> {
  const classId = params.id!;
  const base = `/api/classes/${encodeURIComponent(classId)}/students`;

  const rowHtml = (s: Student): string =>
    `<div class="card roster-row" data-id="${esc(s.id)}" style="display:flex;align-items:center;justify-content:space-between;gap:10px">
       <strong class="rname">${esc(s.display_name)}</strong>
       <span class="row-actions" style="display:flex;gap:14px;white-space:nowrap">
         <a href="#" data-act="rename" class="muted">Rename</a>
         <a href="#" data-act="remove" class="muted">Remove</a>
       </span>
     </div>`;

  async function load(): Promise<void> {
    const { class: cls, students } = await api<RosterResponse>(base);
    app.innerHTML = `
      <div class="topbar"><a data-link href="/classes" class="muted">← My Classes</a></div>
      <h1>${esc(cls.name)}${cls.level ? ` <span class="chip">${esc(cls.level)}</span>` : ""}</h1>

      <h2>Students <span class="muted">(${students.length})</span></h2>
      <div id="roster-list">${
        students.length
          ? students.map(rowHtml).join("")
          : `<p class="empty">No students yet. Paste the class list below.</p>`
      }</div>

      <h2>Add students</h2>
      <p class="hint">Paste the class list — one name per line. You can paste straight from a spreadsheet.</p>
      <textarea id="roster" placeholder="One name per line…"></textarea>
      <div id="err" class="error" hidden></div>
      <div style="height:8px"></div>
      <button class="btn" id="addbtn" type="button">Add students</button>`;
    wire();
  }

  function wire(): void {
    const err = app.querySelector<HTMLElement>("#err")!;
    const showErr = (m: string): void => { err.textContent = m; err.hidden = false; };

    app.querySelector<HTMLButtonElement>("#addbtn")!.addEventListener("click", async () => {
      err.hidden = true;
      const ta = app.querySelector<HTMLTextAreaElement>("#roster")!;
      if (!ta.value.trim()) return;
      try {
        await api(base, { method: "POST", body: JSON.stringify({ names: ta.value }) });
        await load();
      } catch (ex) {
        showErr((ex as Error).message);
      }
    });

    // One delegated handler for rename / remove / save / cancel on the roster rows.
    app.querySelector<HTMLElement>("#roster-list")!.addEventListener("click", async (e) => {
      const a = (e.target as HTMLElement).closest("a[data-act]") as HTMLAnchorElement | null;
      if (!a) return;
      e.preventDefault();
      const rowEl = a.closest<HTMLElement>(".roster-row")!;
      const sid = rowEl.dataset.id!;
      const act = a.dataset.act;

      if (act === "remove") {
        if (a.dataset.confirm !== "1") {
          a.dataset.confirm = "1";
          a.textContent = "Remove? Tap again";
          return;
        }
        try {
          await api(`${base}/${encodeURIComponent(sid)}`, { method: "DELETE" });
          await load();
        } catch (ex) {
          showErr((ex as Error).message);
        }
        return;
      }

      if (act === "rename") {
        const current = rowEl.querySelector<HTMLElement>(".rname")!.textContent ?? "";
        rowEl.innerHTML = `<input class="rename-input" value="${esc(current)}" style="flex:1" />
          <span class="row-actions" style="display:flex;gap:14px;white-space:nowrap">
            <a href="#" data-act="save" class="muted">Save</a>
            <a href="#" data-act="cancel" class="muted">Cancel</a>
          </span>`;
        rowEl.querySelector<HTMLInputElement>(".rename-input")!.focus();
        return;
      }

      if (act === "cancel") {
        await load();
        return;
      }

      if (act === "save") {
        const val = rowEl.querySelector<HTMLInputElement>(".rename-input")!.value.trim();
        if (!val) { await load(); return; }
        try {
          await api(`${base}/${encodeURIComponent(sid)}`, {
            method: "PATCH",
            body: JSON.stringify({ display_name: val }),
          });
          await load();
        } catch (ex) {
          showErr((ex as Error).message);
        }
      }
    });
  }

  await load();
}
